import { Component } from '@theme/component';
import { MegaMenuHoverEvent } from '@theme/events';

/** Delay before announcing a committed hover (used by listeners to preload). */
const HOVER_COMMIT_DELAY_MS = 100;

/**
 * Header menu, lean rewrite (2026-08-05).
 *
 * The previous version measured submenu heights on every hover (including a
 * display:none toggle across all submenus), wrote size custom-properties that
 * invalidated the whole header subtree, tracked every pointermove on
 * document.body, and re-dispatched SYNTHETIC pointerenter events from
 * elementFromPoint after 50ms idle timers. Under main-thread load those timers
 * landed late and re-opened panels after the pointer had left - the "stuck
 * open / bounces" bug. All of that is gone: panel visibility is pure CSS
 * (:hover / :focus-within), driven by the browser's hit-tester, which cannot
 * lag. This class only keeps aria state for assistive tech and fires
 * MegaMenuHoverEvent for listeners that preload submenu content.
 *
 * @extends {Component<{ overflowMenu: HTMLElement }>}
 */
class HeaderMenu extends Component {
  requiredRefs = ['overflowMenu'];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #hoverTimer;

  connectedCallback() {
    super.connectedCallback();
    // ONE attribute on #header-component replaces four :has() rules that were
    // re-evaluating style across the whole header subtree on every item
    // switch (~1s of recalc per hover on a mid machine). Enter/leave of the
    // menu as a whole is two flips per visit, not one per item.
    const hc = this.closest('#header-component');
    if (!hc) return;
    const open = () => hc.setAttribute('data-menu-open', '');
    const close = (e) => {
      const to = e && 'relatedTarget' in e ? e.relatedTarget : null;
      if (to instanceof Node && this.contains(to)) return;
      hc.removeAttribute('data-menu-open');
    };
    this.addEventListener('pointerenter', open);
    this.addEventListener('pointerleave', close);
    this.addEventListener('focusin', open);
    this.addEventListener('focusout', close);
  }

  get overflowMenu() {
    return /** @type {HTMLElement | null} */ (
      this.refs.overflowMenu?.shadowRoot?.querySelector('[part="overflow"]') ?? null
    );
  }

  /** @param {PointerEvent | FocusEvent} event */
  activate = (event) => {
    if (!(event.target instanceof Element)) return;
    const item = findMenuItem(event.target);
    if (!item) return;

    for (const el of this.querySelectorAll('[ref="menuitem"][aria-expanded="true"]')) {
      if (el !== item) el.setAttribute('aria-expanded', 'false');
    }
    item.setAttribute('aria-expanded', 'true');
    this.ariaExpanded = 'true';
    this.dataset.overflowExpanded = String(event.target.slot !== '');

    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = setTimeout(() => this.dispatchEvent(new MegaMenuHoverEvent()), HOVER_COMMIT_DELAY_MS);
  };

  /** @param {PointerEvent | FocusEvent} event */
  deactivate = (event) => {
    // Bookkeeping only - CSS has already hidden the panel. Skip the reset when
    // the pointer/focus merely moved elsewhere inside the menu.
    const to = event && 'relatedTarget' in event ? event.relatedTarget : null;
    if (to instanceof Node && this.contains(to)) return;
    if (this.overflowMenu?.matches(':hover')) return;

    clearTimeout(this.#hoverTimer);
    this.#hoverTimer = undefined;
    for (const el of this.querySelectorAll('[ref="menuitem"][aria-expanded="true"]')) {
      el.setAttribute('aria-expanded', 'false');
    }
    this.ariaExpanded = 'false';
    this.dataset.overflowExpanded = 'false';
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    clearTimeout(this.#hoverTimer);
  }
}

if (!customElements.get('header-menu')) {
  customElements.define('header-menu', HeaderMenu);
}

/**
 * Find the menu item for an event target ("More" maps to its first overflow item).
 * @param {Element | null | undefined} element
 * @returns {HTMLElement | null}
 */
function findMenuItem(element) {
  if (!(element instanceof Element)) return null;
  if (element.matches('[slot="more"]')) {
    return findMenuItem(element.parentElement?.querySelector('[slot="overflow"]'));
  }
  return element.querySelector('[ref="menuitem"]');
}
